import 'plyr/dist/plyr.css';
import Plyr from 'plyr';

export default class PodcastPlayer {
    static init() {
        try {
            document.querySelectorAll('.podcast-player').forEach(element => {
                if (element) {
                    new PodcastPlayer(element);
                }
            });
        } catch (error) {
            console.error('Error initializing PodcastPlayer:', error);
        }
    }

    constructor(element) {
        this.element = element;
        if (!this.element) {
            return;
        }

        this.audioElement = this.element.querySelector('.podcast-player__audio');
        if (!this.audioElement) {
            console.warn('PodcastPlayer: Audio element not found', this.element);
            return;
        }

        // Initialize Plyr player
        this.player = new Plyr(this.audioElement, {
            controls: ['play-large', 'play', 'progress', 'current-time', 'duration', 'mute', 'volume', 'settings', 'pip'],
            settings: ['speed'],
            speed: {selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]},
            tooltips: {controls: true, seek: true},
            i18n: this.getI18n(),
            invertTime: false
        });
        document.querySelector('.podcast-player__plyr-loading-indicator')?.remove();

        // Set up transcript if available
        element.querySelector('.podcast-player__transcript-button--open')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.element.classList.add('podcast-player--show-transcript');
            this.element.querySelector('details').open = true;
        });
        element.querySelector('.podcast-player__transcript-button--close')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.element.classList.remove('podcast-player--show-transcript');
            this.element.querySelector('details').open = false;
        });
        element.querySelector('details summary')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.element.classList.remove('podcast-player--show-transcript');
            this.element.querySelector('details').open = false;
        });
    }

    getI18n() {
        const lang = document.querySelector('html').getAttribute('lang').substring(0, 2) || 'en';

        if (lang === 'de') {
            return {
                "restart": "Neu starten",
                "rewind": "{seektime}s zurückspulen",
                "play": "Wiedergabe",
                "pause": "Pause",
                "fastForward": "{seektime}s vorspulen",
                "seek": "Springen",
                "seekLabel": "{currentTime} von {duration}",
                "played": "Gespielt",
                "buffered": "Gepuffert",
                "currentTime": "Aktuelle Zeit",
                "duration": "Dauer",
                "volume": "Lautstärke",
                "mute": "Stumm schalten",
                "unmute": "Ton einschalten",
                "enableCaptions": "Untertitel aktivieren",
                "disableCaptions": "Untertitel deaktivieren",
                "download": "Herunterladen",
                "enterFullscreen": "Vollbild aktivieren",
                "exitFullscreen": "Vollbild verlassen",
                "frameTitle": "Player für {title}",
                "captions": "Untertitel",
                "settings": "Einstellungen",
                "pip": "Bild-im-Bild",
                "menuBack": "Zurück zum vorherigen Menü",
                "speed": "Geschwindigkeit",
                "normal": "Normal",
                "quality": "Qualität",
                "loop": "Wiederholen",
                "start": "Start",
                "end": "Ende",
                "all": "Alle",
                "reset": "Zurücksetzen",
                "disabled": "Deaktiviert",
                "enabled": "Aktiviert",
                "advertisement": "Werbung",
                "qualityBadge": {
                    "2160": "4K",
                    "1440": "HD",
                    "1080": "HD",
                    "720": "HD",
                    "576": "SD",
                    "480": "SD"
                }
            };
        }
        if (lang === 'it') {
            return {
                "restart": "Riavvia",
                "rewind": "Indietro di {seektime}s",
                "play": "Riproduci",
                "pause": "Pausa",
                "fastForward": "Avanti di {seektime}s",
                "seek": "Cerca",
                "seekLabel": "{currentTime} di {duration}",
                "played": "Riprodotto",
                "buffered": "Bufferizzato",
                "currentTime": "Tempo corrente",
                "duration": "Durata",
                "volume": "Volume",
                "mute": "Silenzia",
                "unmute": "Riattiva audio",
                "enableCaptions": "Attiva sottotitoli",
                "disableCaptions": "Disattiva sottotitoli",
                "download": "Scarica",
                "enterFullscreen": "Schermo intero",
                "exitFullscreen": "Esci da schermo intero",
                "frameTitle": "Player per {title}",
                "captions": "Sottotitoli",
                "settings": "Impostazioni",
                "pip": "PIP",
                "menuBack": "Torna al menu precedente",
                "speed": "Velocità",
                "normal": "Normale",
                "quality": "Qualità",
                "loop": "Ripeti",
                "start": "Inizio",
                "end": "Fine",
                "all": "Tutti",
                "reset": "Reimposta",
                "disabled": "Disattivato",
                "enabled": "Attivato",
                "advertisement": "Pubblicità",
                "qualityBadge": {
                    "2160": "4K",
                    "1440": "HD",
                    "1080": "HD",
                    "720": "HD",
                    "576": "SD",
                    "480": "SD"
                }
            };
        }

        return {
            restart: 'Restart',
            rewind: 'Rewind {seektime}s',
            play: 'Play',
            pause: 'Pause',
            fastForward: 'Forward {seektime}s',
            seek: 'Seek',
            seekLabel: '{currentTime} of {duration}',
            played: 'Played',
            buffered: 'Buffered',
            currentTime: 'Current time',
            duration: 'Duration',
            volume: 'Volume',
            mute: 'Mute',
            unmute: 'Unmute',
            enableCaptions: 'Enable captions',
            disableCaptions: 'Disable captions',
            download: 'Download',
            enterFullscreen: 'Enter fullscreen',
            exitFullscreen: 'Exit fullscreen',
            frameTitle: 'Player for {title}',
            captions: 'Captions',
            settings: 'Settings',
            pip: 'PIP',
            menuBack: 'Go back to previous menu',
            speed: 'Speed',
            normal: 'Normal',
            quality: 'Quality',
            loop: 'Loop',
            start: 'Start',
            end: 'End',
            all: 'All',
            reset: 'Reset',
            disabled: 'Disabled',
            enabled: 'Enabled',
            advertisement: 'Ad',
            qualityBadge: {
                2160: '4K',
                1440: 'HD',
                1080: 'HD',
                720: 'HD',
                576: 'SD',
                480: 'SD',
            },
        };
    }
}
